import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useT } from '../context/LocaleContext';
import StaffPanel from '../components/StaffPanel';
import ConfirmBar from '../components/ConfirmBar';
import Sheet from '../components/Sheet';
import { useAuth } from '../context/AuthContext';
import { defaultBusinessProfile, fetchBusinessProfile } from '../lib/businessProfile';
import { supabase } from '../lib/supabase';
import BrandsCatalog from './BrandsCatalog';
import HoursPage from './Hours';

const ADMIN_ROLES = [
  { value: 'manager', label: 'Admin' },
  { value: 'advisor', label: 'Advisor' },
];

const MECHANIC_ROLES = [{ value: 'technician', label: 'Mechanic' }];

const blankBranch = {
  name: '',
  address: '',
  phone: '',
  map_url: '',
  slot_minutes: 30,
  parallel_slots: 1,
};

const OLD_TABS = {
  hours: 'hours',
  branches: 'locations',
  brands: 'brands',
  admins: 'team',
  mechanics: 'team',
};

function weekHours(branchId) {
  return [1, 2, 3, 4, 5, 6, 7].map((weekday) => ({
    branch_id: branchId,
    weekday,
    opens_at: '09:00',
    closes_at: '18:00',
    is_closed: weekday === 5,
  }));
}

export function SetupHome() {
  const t = useT();
  const [params] = useSearchParams();
  const legacy = OLD_TABS[params.get('tab')];
  if (legacy) return <Navigate to={`/management/${legacy}`} replace />;

  return (
    <section>
      <header className="page-head">
        <div>
          <span className="kicker">{t('setup.kicker')}</span>
          <h1>{t('setup.title')}</h1>
          <p className="lede">{t('setup.lede')}</p>
        </div>
      </header>
      <div className="setup-grid">
        <SetupTile
          to="/management/locations"
          icon="location_on"
          title={t('setup.locations')}
          copy={t('setup.locationsCopy')}
        />
        <SetupTile
          to="/management/hours"
          icon="schedule"
          title={t('setup.hours')}
          copy={t('setup.hoursCopy')}
        />
        <SetupTile
          to="/management/brands"
          icon="directions_car"
          title={t('setup.brands')}
          copy={t('setup.brandsCopy')}
        />
        <SetupTile
          to="/management/team"
          icon="badge"
          title={t('setup.team')}
          copy={t('setup.teamCopy')}
        />
        <SetupTile
          to="/management/business"
          icon="storefront"
          title={t('setup.business')}
          copy={t('setup.businessCopy')}
        />
      </div>
    </section>
  );
}

function SetupTile({ to, icon, title, copy }) {
  const t = useT();
  return (
    <Link to={to} className="setup-tile">
      <span className="material-symbol" aria-hidden="true">
        {icon}
      </span>
      <h2>{title}</h2>
      <p>{copy}</p>
      <span className="setup-tile-go">{t('setup.open')}</span>
    </Link>
  );
}

function SetupBack() {
  const t = useT();
  return (
    <Link to="/management" className="back">
      <span className="material-symbol" aria-hidden="true">
        arrow_back
      </span>
      {t('setup.back')}
    </Link>
  );
}

export function LocationsPage() {
  const t = useT();
  const { isManager } = useAuth();
  return (
    <section>
      <SetupBack />
      <header className="page-head">
        <div>
          <span className="kicker">{t('setup.kicker')}</span>
          <h1>{t('setup.locations')}</h1>
          <p className="lede">
            {t('setup.locationsLede')}
          </p>
        </div>
      </header>
      <BranchesPanel isManager={isManager} />
    </section>
  );
}

export function HoursSetupPage() {
  return (
    <section>
      <SetupBack />
      <HoursPage />
    </section>
  );
}

export function BrandsSetupPage() {
  const t = useT();
  const { isManager } = useAuth();
  return (
    <section>
      <SetupBack />
      <header className="page-head">
        <div>
          <span className="kicker">{t('setup.kicker')}</span>
          <h1>{t('setup.brands')}</h1>
          <p className="lede">{t('setup.brandsLede')}</p>
        </div>
      </header>
      <BrandsCatalog isManager={isManager} />
    </section>
  );
}

export function TeamPage() {
  const t = useT();
  const { isManager } = useAuth();
  return (
    <section>
      <SetupBack />
      <header className="page-head">
        <div>
          <span className="kicker">{t('setup.kicker')}</span>
          <h1>{t('setup.team')}</h1>
          <p className="lede">{t('setup.teamLede')}</p>
        </div>
      </header>

      <h2 className="setup-section">{t('setup.admins')}</h2>
      <p className="lede" style={{ margin: '0 0 12px' }}>
        {t('setup.adminsLede')}
      </p>
      <StaffPanel
        isManager={isManager}
        roles={['manager', 'advisor']}
        roleOptions={ADMIN_ROLES.map((role) => ({
          ...role,
          label: t(`role.${role.value}`),
        }))}
        defaultRole="manager"
        addLabel={t('setup.addAdmin')}
        createTitle={t('setup.addAdmin')}
        emptyText={t('setup.noAdmins')}
        tabAccess
        tabAccessRoles={['advisor']}
        allowPasswordReset
      />

      <h2 className="setup-section">{t('setup.mechanics')}</h2>
      <p className="lede" style={{ margin: '0 0 12px' }}>
        {t('setup.teamCopy')}
      </p>
      <StaffPanel
        isManager={isManager}
        roles={['technician']}
        roleOptions={MECHANIC_ROLES.map((role) => ({
          ...role,
          label: t(`role.${role.value}`),
        }))}
        defaultRole="technician"
        addLabel={t('mechanic.add')}
        createTitle={t('mechanic.add')}
        emptyText={t('setup.noMechanics')}
        optionalAdd
        requireBranch
        tabAccess
        allowPasswordReset
      />
    </section>
  );
}

export function BusinessSetupPage() {
  const t = useT();
  const { isManager } = useAuth();
  const [draft, setDraft] = useState(defaultBusinessProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  useEffect(() => {
    let alive = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const next = await fetchBusinessProfile();
        if (!alive) return;
        setDraft(next);
      } catch (e) {
        if (!alive) return;
        setError(e.message || t('setup.loadFailed'));
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  async function save(event) {
    event.preventDefault();
    if (!isManager) return;
    setSaving(true);
    setError('');
    setOk('');
    const payload = {
      id: true,
      brand_name: draft.brand_name?.trim() || defaultBusinessProfile.brand_name,
      legal_name: draft.legal_name?.trim() || null,
      tagline: draft.tagline?.trim() || null,
      address: draft.address?.trim() || null,
      phone_primary: draft.phone_primary?.trim() || null,
      phone_secondary: draft.phone_secondary?.trim() || null,
      support_email: draft.support_email?.trim() || null,
      website: draft.website?.trim() || null,
      logo_url: draft.logo_url?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const { error: saveError } = await supabase
      .from('business_profile')
      .upsert(payload, { onConflict: 'id' });
    setSaving(false);
    if (saveError) {
      setError(saveError.message);
      return;
    }
    setOk(t('setup.saved'));
  }

  return (
    <section>
      <SetupBack />
      <header className="page-head">
        <div>
          <span className="kicker">{t('setup.kicker')}</span>
          <h1>{t('setup.businessTitle')}</h1>
          <p className="lede">
            {t('setup.businessCopy')}
          </p>
        </div>
      </header>
      {error && <p className="form-error">{error}</p>}
      {ok && <p className="form-ok">{ok}</p>}
      {loading ? (
        <p className="empty">{t('setup.loadingBusiness')}</p>
      ) : (
        <article className="panel">
          <form className="sheet-form" onSubmit={save}>
            <div className="form-row">
              <label>
                {t('setup.brandName')}
                <input
                  required
                  value={draft.brand_name || ''}
                  onChange={(e) => setDraft({ ...draft, brand_name: e.target.value })}
                />
              </label>
              <label>
                {t('setup.legalName')}
                <input
                  value={draft.legal_name || ''}
                  onChange={(e) => setDraft({ ...draft, legal_name: e.target.value })}
                />
              </label>
            </div>
            <label>
              {t('setup.tagline')}
              <input
                value={draft.tagline || ''}
                onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}
              />
            </label>
            <label>
              {t('setup.address')}
              <textarea
                rows={2}
                value={draft.address || ''}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })}
              />
            </label>
            <div className="form-row">
              <label>
                {t('setup.phonePrimary')}
                <input
                  value={draft.phone_primary || ''}
                  onChange={(e) => setDraft({ ...draft, phone_primary: e.target.value })}
                />
              </label>
              <label>
                {t('setup.phoneSecondary')}
                <input
                  value={draft.phone_secondary || ''}
                  onChange={(e) => setDraft({ ...draft, phone_secondary: e.target.value })}
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                {t('setup.supportEmail')}
                <input
                  type="email"
                  value={draft.support_email || ''}
                  onChange={(e) => setDraft({ ...draft, support_email: e.target.value })}
                />
              </label>
              <label>
                {t('setup.website')}
                <input
                  type="url"
                  placeholder="https://"
                  value={draft.website || ''}
                  onChange={(e) => setDraft({ ...draft, website: e.target.value })}
                />
              </label>
            </div>
            <label>
              {t('setup.logoUrl')}
              <input
                type="url"
                placeholder="https://.../logo.png"
                value={draft.logo_url || ''}
                onChange={(e) => setDraft({ ...draft, logo_url: e.target.value })}
              />
            </label>
            <div className="row-actions">
              <button type="submit" className="primary" disabled={saving || !isManager}>
                {saving ? t('common.saving') : t('setup.saveBusiness')}
              </button>
            </div>
          </form>
        </article>
      )}
    </section>
  );
}

function BranchesPanel({ isManager }) {
  const t = useT();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState(blankBranch);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [openBranch, setOpenBranch] = useState(null);

  async function load() {
    const { data, error: queryError } = await supabase
      .from('branches')
      .select('*')
      .order('name');
    if (queryError) {
      setError(queryError.message);
      return;
    }
    setRows(data || []);
  }

  useEffect(() => {
    load();
  }, []);

  function startCreate() {
    setEditingId(null);
    setDraft({ ...blankBranch });
    setOpen(true);
  }

  function startEdit(row) {
    setEditingId(row.id);
    setDraft({
      name: row.name || '',
      address: row.address || '',
      phone: row.phone || '',
      map_url: row.map_url || '',
      slot_minutes: row.slot_minutes ?? 30,
      parallel_slots: row.parallel_slots ?? 1,
    });
    setOpen(true);
  }

  async function save(event) {
    event.preventDefault();
    if (!isManager) return;
    setSaving(true);
    setError('');
    const payload = {
      name: draft.name.trim(),
      address: draft.address.trim() || null,
      phone: draft.phone.trim() || null,
      map_url: draft.map_url.trim() || null,
      slot_minutes: Number(draft.slot_minutes) || 30,
      parallel_slots: Math.min(2, Math.max(1, Number(draft.parallel_slots) || 2)),
    };
    if (editingId) {
      const { error: saveError } = await supabase
        .from('branches')
        .update(payload)
        .eq('id', editingId);
      setSaving(false);
      if (saveError) {
        setError(saveError.message);
        return;
      }
    } else {
      const { data, error: saveError } = await supabase
        .from('branches')
        .insert(payload)
        .select('id')
        .single();
      if (saveError) {
        setSaving(false);
        setError(saveError.message);
        return;
      }
      const { error: hoursError } = await supabase
        .from('branch_hours')
        .insert(weekHours(data.id));
      setSaving(false);
      if (hoursError) {
        setError(hoursError.message);
        return;
      }
    }
    setOpen(false);
    load();
  }

  async function remove(id) {
    if (!isManager) return;
    const { error: deleteError } = await supabase.from('branches').delete().eq('id', id);
    if (deleteError) {
      setError(deleteError.message);
      return;
    }
    setPendingDelete(null);
    load();
  }

  return (
    <>
      <div className="panel-head">
        <span />
        {isManager && (
          <button type="button" className="primary" onClick={startCreate}>
            {t('setup.addLocation')}
          </button>
        )}
      </div>
      {error && <p className="form-error">{error}</p>}
      {rows.length === 0 ? (
        <p className="empty">{t('setup.noLocations')}</p>
      ) : (
        <div className="booking-list">
          {rows.map((row) => {
            const expanded = openBranch === row.id;
            return (
              <article key={row.id} className="fold-card">
                <button
                  type="button"
                  className="desk-row as-button"
                  onClick={() => setOpenBranch(expanded ? null : row.id)}
                >
                  <div className="desk-main">
                    <h3>{row.name}</h3>
                    <p>{row.address || t('setup.noAddress')}</p>
                  </div>
                  <span className="material-symbol desk-chevron">
                    {expanded ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {expanded && (
                  <div className="fold-body">
                    <p>
                      {t('setup.phone')} {row.phone || '—'}
                      {row.map_url ? (
                        <>
                          {' · '}
                          <a href={row.map_url} target="_blank" rel="noreferrer">
                            {t('setup.openMap')}
                          </a>
                        </>
                      ) : null}
                    </p>
                    <p>
                      <Link to={`/management/hours?branch=${row.id}`}>{t('setup.setHours')}</Link>
                    </p>
                    {isManager &&
                      (pendingDelete === row.id ? (
                        <ConfirmBar
                          message={t('setup.removeLocation')}
                          confirmLabel={t('common.remove')}
                          onCancel={() => setPendingDelete(null)}
                          onConfirm={() => remove(row.id)}
                        />
                      ) : (
                        <div className="row-actions" style={{ marginTop: 12 }}>
                          <button type="button" className="ghost" onClick={() => startEdit(row)}>
                            {t('common.edit')}
                          </button>
                          <button
                            type="button"
                            className="ghost danger"
                            onClick={() => setPendingDelete(row.id)}
                          >
                            {t('common.remove')}
                          </button>
                        </div>
                      ))}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
      {open && (
        <Sheet
          variant="modal"
          title={editingId ? t('setup.editLocation') : t('setup.newLocation')}
          subtitle={t('setup.locations')}
          onClose={() => setOpen(false)}
        >
          <form className="sheet-form" onSubmit={save}>
            <label>
              {t('setup.name')}
              <input
                required
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label>
              {t('setup.address')}
              <input
                value={draft.address}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })}
              />
            </label>
            <label>
              {t('setup.phone')}
              <input
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </label>
            <label>
              {t('setup.mapLink')}
              <input
                type="url"
                placeholder="https://maps.google.com/..."
                value={draft.map_url}
                onChange={(e) => setDraft({ ...draft, map_url: e.target.value })}
              />
            </label>
            <p className="lede" style={{ marginTop: 0 }}>
              {t('setup.mapHint')}
            </p>
            <div className="row-actions">
              <button type="submit" className="primary" disabled={saving}>
                {saving ? t('common.saving') : t('common.save')}
              </button>
              <button type="button" className="ghost" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Sheet>
      )}
    </>
  );
}
